import { NextResponse } from 'next/server'
import { query, queryOne } from '@/lib/db'

export async function GET(req: Request) {
  const { searchParams } = new URL(req.url)
  const shopId = searchParams.get('shop_id')
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  const toEnd = `${to} 23:59:59`

  const [ventesAgg, lignesAgg, parJour, chargesAgg, topArticles, kardeEnCours, clientCashAgg] =
    await Promise.all([

    // CA & encaissé — depuis ventes SEULEMENT (pas de jointure → pas de double-comptage)
    queryOne<any>(`
      SELECT
        COUNT(*)                                       AS nb_ventes,
        COALESCE(SUM(total), 0)                        AS ca_total,
        COALESCE(SUM(COALESCE(montant_encaisse, total)), 0) AS encaisse
      FROM ventes
      WHERE shop_id = ? AND created_at BETWEEN ? AND ?
    `, [shopId, from, toEnd]),

    // Coût & bénéfice brut — depuis vente_lignes SEULEMENT
    queryOne<any>(`
      SELECT
        COALESCE(SUM(vl.prix_unitaire * vl.quantite), 0)                          AS ca_lignes,
        COALESCE(SUM(vl.ras_mel_unitaire * vl.quantite), 0)                       AS cout,
        COALESCE(SUM((vl.prix_unitaire - vl.ras_mel_unitaire) * vl.quantite), 0)  AS benefice_brut
      FROM vente_lignes vl
      JOIN ventes v ON vl.vente_id = v.id
      WHERE v.shop_id = ? AND v.created_at BETWEEN ? AND ?
    `, [shopId, from, toEnd]),

    // Ventes par jour (CA + encaissé)
    query<any>(`
      SELECT DATE(created_at) AS date,
        SUM(total)                          AS ca_total,
        SUM(COALESCE(montant_encaisse, total)) AS encaisse
      FROM ventes
      WHERE shop_id = ? AND created_at BETWEEN ? AND ?
      GROUP BY DATE(created_at)
      ORDER BY date
    `, [shopId, from, toEnd]),

    // Charges
    queryOne<any>(`
      SELECT COALESCE(SUM(montant), 0) AS total
      FROM charges
      WHERE shop_id = ? AND date BETWEEN ? AND ?
    `, [shopId, from, to]),

    // Top 5 articles (CA + bénéfice)
    query<any>(`
      SELECT a.name,
        SUM(vl.quantite)                                              AS qty,
        SUM(vl.prix_unitaire * vl.quantite)                           AS ca,
        SUM((vl.prix_unitaire - vl.ras_mel_unitaire) * vl.quantite)   AS benefice
      FROM vente_lignes vl
      JOIN ventes v   ON vl.vente_id = v.id
      JOIN articles a ON vl.article_id = a.id
      WHERE v.shop_id = ? AND v.created_at BETWEEN ? AND ?
      GROUP BY a.id, a.name
      ORDER BY ca DESC
      LIMIT 5
    `, [shopId, from, toEnd]),

    // Kardé en cours — snapshot ACTUEL (toutes périodes), dettes non soldées
    queryOne<any>(`
      SELECT COUNT(*) AS nb_clients, COALESCE(SUM(-solde), 0) AS total_dette
      FROM clients
      WHERE shop_id = ? AND solde < 0
    `, [shopId]),

    // Cash client encaissé sur la période (paiements + avances standalone)
    queryOne<any>(`
      SELECT
        COALESCE(SUM(CASE WHEN ct.type = 'paiement' THEN ct.montant ELSE 0 END), 0) AS paiements,
        COALESCE(SUM(CASE WHEN ct.type = 'avance'   THEN ct.montant ELSE 0 END), 0) AS avances
      FROM client_transactions ct
      JOIN clients cl ON ct.client_id = cl.id
      WHERE cl.shop_id = ? AND ct.vente_id IS NULL
        AND ct.created_at BETWEEN ? AND ?
    `, [shopId, from, toEnd]),
  ])

  const ca_total      = parseFloat(ventesAgg?.ca_total || 0)
  const encaisse      = parseFloat(ventesAgg?.encaisse || 0)
  const karde_cree    = Math.max(0, ca_total - encaisse)
  const cout          = parseFloat(lignesAgg?.cout || 0)
  const benefice_brut = parseFloat(lignesAgg?.benefice_brut || 0)
  const total_charges = parseFloat(chargesAgg?.total || 0)
  const benefice_net  = benefice_brut - total_charges
  const paiements_clients = parseFloat(clientCashAgg?.paiements || 0)
  const avances_clients   = parseFloat(clientCashAgg?.avances || 0)

  // Cash réellement entré sur la période (lié à l'activité commerciale)
  const cash_encaisse_total = encaisse + paiements_clients + avances_clients

  return NextResponse.json({
    nb_ventes: parseInt(ventesAgg?.nb_ventes || 0),

    // P&L (accrual — sur marchandise vendue)
    ca_total,
    cout,
    benefice_brut,
    total_charges,
    benefice_net,

    // Trésorerie (cash)
    encaisse,
    karde_cree,
    paiements_clients,
    avances_clients,
    cash_encaisse_total,

    // Créances (snapshot)
    karde_en_cours: {
      nb_clients: parseInt(kardeEnCours?.nb_clients || 0),
      total_dette: parseFloat(kardeEnCours?.total_dette || 0),
    },

    top_articles: topArticles,
    ventes_par_jour: parJour,
  })
}
